import React, { useState } from "react";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import PrimaryButton from "../Buttons/PrimaryButton";
import OutlinedButton from "../Buttons/OutlinedButton";

type Props = {
  visible: boolean;
  imageData: string; // base64 image data
  metrics: {
    timestamp?: number;
    total_image_area?: number;
    bread_area?: number;
    bubble_area?: number;
    air_ratio_percent?: number;
    num_holes?: number;
    avg_hole_area?: number;
    median_hole_area?: number;
    std_hole_area?: number;
    min_hole_area?: number;
    max_hole_area?: number;
    avg_diameter?: number;
  };
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
};

const CapturePreviewModal = (props: Props) => {
  const handleConfirm = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (props.loading) return; // Prevent multiple clicks
    props.onConfirm();
  };

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (props.loading) return; // Prevent cancel during save
    props.onCancel();
  };

  if (!props.visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .capture-preview-modal-content {
          background: #21282c;
          border: 2px solid #ff9800;
          border-radius: 16px;
          padding: 0;
          max-width: 900px;
          width: 100%;
          max-height: calc(100vh - 40px);
          overflow-y: auto;
          box-shadow: 0 0 60px rgba(255, 152, 0, 0.4);
          position: relative;
          animation: scaleIn 0.3s ease;
        }

        .capture-preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem 2rem;
          border-bottom: 2px solid rgba(255, 152, 0, 0.3);
        }

        .capture-preview-header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .capture-preview-icon {
          font-size: 2rem;
          background: rgba(255, 152, 0, 0.2);
          padding: 0.5rem;
          border-radius: 8px;
          border: 1px solid rgba(255, 152, 0, 0.4);
        }

        .capture-preview-title-section h2 {
          margin: 0;
          color: #ff9800;
          font-size: 1.5rem;
          font-weight: bold;
          text-shadow: 0 0 20px rgba(255, 152, 0, 0.5);
          letter-spacing: 1px;
        }

        .capture-preview-title-section p {
          margin: 0.25rem 0 0 0;
          color: #6c757d;
          font-size: 0.85rem;
          font-family: monospace;
        }

        .capture-preview-close-btn {
          background: transparent;
          border: 2px solid #dc3545;
          border-radius: 8px;
          color: #dc3545;
          font-size: 1.5rem;
          width: 40px;
          height: 40px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .capture-preview-close-btn:hover {
          background: #dc3545;
          color: #fff;
          box-shadow: 0 0 20px #dc354560;
        }

        .capture-preview-body {
          padding: 2rem;
        }

        .capture-preview-image-container {
          width: 100%;
          margin-bottom: 2rem;
          border-radius: 12px;
          overflow: hidden;
          border: 2px solid rgba(255, 152, 0, 0.3);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .capture-preview-image-container img {
          width: 100%;
          height: auto;
          display: block;
        }

        .capture-preview-metrics-section h3 {
          margin: 0 0 1.5rem 0;
          color: #ff9800;
          font-size: 1.2rem;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .capture-preview-metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .capture-preview-metric-card {
          background: #171a1c;
          border: 2px solid rgba(255, 152, 0, 0.3);
          border-radius: 12px;
          padding: 1rem;
          transition: all 0.3s ease;
        }

        .capture-preview-metric-card:hover {
          border-color: #ff9800;
          box-shadow: 0 0 20px rgba(255, 152, 0, 0.2);
          transform: translateY(-2px);
        }

        .capture-preview-metric-label {
          font-size: 0.75rem;
          color: #ff9800;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 0.5rem;
          font-weight: bold;
        }

        .capture-preview-metric-value {
          font-size: 1.5rem;
          font-weight: bold;
          color: #fff;
          font-family: monospace;
        }

        .capture-preview-metric-unit {
          font-size: 0.9rem;
          color: #6c757d;
          margin-left: 0.25rem;
        }

        .capture-preview-footer {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding: 1.5rem 2rem;
          border-top: 2px solid rgba(255, 152, 0, 0.3);
        }

        .capture-preview-cancel-btn {
          padding: 0.75rem 1.5rem;
          background: transparent;
          border: 2px solid #6c757d;
          border-radius: 8px;
          color: #6c757d;
          font-size: 0.9rem;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 1px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .capture-preview-cancel-btn:hover {
          border-color: #fff;
          color: #fff;
        }

        .capture-preview-confirm-btn {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, rgba(255, 152, 0, 0.4) 0%, rgba(255, 152, 0, 0.2) 100%);
          border: 2px solid #ff9800;
          border-radius: 8px;
          color: #ff9800;
          font-size: 0.9rem;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 1px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 0 20px rgba(255, 152, 0, 0.4);
        }

        .capture-preview-confirm-btn:hover {
          background: linear-gradient(135deg, rgba(255, 152, 0, 0.5) 0%, rgba(255, 152, 0, 0.3) 100%);
          box-shadow: 0 0 40px rgba(255, 152, 0, 0.6);
          transform: translateY(-2px);
        }

        .capture-preview-confirm-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      <div className="capture-preview-modal-content">
        {/* Header */}
        <div className="capture-preview-header">
          <div className="capture-preview-header-left">
            <div className="capture-preview-icon">📷</div>
            <div className="capture-preview-title-section">
              <h2>CAPTURE PREVIEW</h2>
              <p>Review image and metrics before saving</p>
            </div>
          </div>
          <button
            className="capture-preview-close-btn"
            onClick={handleCancel}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="capture-preview-body">
          {/* Image Preview */}
          <div className="capture-preview-image-container">
            <img
              src={`data:image/jpeg;base64,${props.imageData}`}
              alt="Captured preview"
            />
          </div>

          {/* Metrics Section */}
          <div className="capture-preview-metrics-section">
            <h3>
              <span>📊</span>
              <span>HOLE DETECTION METRICS</span>
            </h3>
            <div className="capture-preview-metrics-grid">
              {/* Air Ratio */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">
                  Air/Dough Ratio
                </div>
                <div className="capture-preview-metric-value">
                  {props.metrics.air_ratio_percent?.toFixed(2) || "0.00"}
                  <span className="capture-preview-metric-unit">%</span>
                </div>
              </div>

              {/* Hole Count */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">Hole Count</div>
                <div className="capture-preview-metric-value">
                  {props.metrics.num_holes || 0}
                </div>
              </div>

              {/* Average Hole Area */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">
                  Avg Hole Area
                </div>
                <div className="capture-preview-metric-value">
                  {props.metrics.avg_hole_area?.toFixed(4) || "0.0000"}
                  <span className="capture-preview-metric-unit">cm²</span>
                </div>
              </div>

              {/* Median Hole Area */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">
                  Median Area
                </div>
                <div className="capture-preview-metric-value">
                  {props.metrics.median_hole_area?.toFixed(4) || "0.0000"}
                  <span className="capture-preview-metric-unit">cm²</span>
                </div>
              </div>

              {/* Min Hole Area */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">
                  Min Hole Area
                </div>
                <div className="capture-preview-metric-value">
                  {props.metrics.min_hole_area?.toFixed(4) || "0.0000"}
                  <span className="capture-preview-metric-unit">cm²</span>
                </div>
              </div>

              {/* Max Hole Area */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">
                  Max Hole Area
                </div>
                <div className="capture-preview-metric-value">
                  {props.metrics.max_hole_area?.toFixed(4) || "0.0000"}
                  <span className="capture-preview-metric-unit">cm²</span>
                </div>
              </div>

              {/* Bread Area */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">Bread Area</div>
                <div className="capture-preview-metric-value">
                  {props.metrics.bread_area?.toFixed(2) || "0.00"}
                  <span className="capture-preview-metric-unit">cm²</span>
                </div>
              </div>

              {/* Average Diameter */}
              <div className="capture-preview-metric-card">
                <div className="capture-preview-metric-label">
                  Avg Diameter
                </div>
                <div className="capture-preview-metric-value">
                  {props.metrics.avg_diameter?.toFixed(2) || "0.00"}
                  <span className="capture-preview-metric-unit">cm</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="capture-preview-footer">
          <button
            className="capture-preview-cancel-btn"
            onClick={handleCancel}
            disabled={props.loading}
          >
            Cancel
          </button>
          <button
            className="capture-preview-confirm-btn"
            onClick={handleConfirm}
            disabled={props.loading}
          >
            {props.loading ? "Saving..." : "✓ Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CapturePreviewModal;
